import type { MessageDescriptor } from 'react-intl'

import { FORMATS_MESSAGES } from '@app/constants/Messages/formats'

import type { Moment } from './moment'
import moment from './moment'
import { uppercaseFirstLetter } from './uppercaseFirstLetter'

/**
 * When an order starts and ends, on the clock it is shown by. A day or more is multi-day, as the
 * backend counts it: an overnight 22:00–02:00 is not. An online order is shown in the viewer's zone;
 * an offline one on its own wall clock, the end date taken from the span's length, since `date` names
 * only the first day. That sum is done in UTC, as the backend does it: in the browser's zone a
 * daylight-saving switch inside the span would move the end an hour, and past midnight near it.
 */
export class OrderPeriod {
  readonly start: Moment
  readonly end: Moment
  readonly multiDay: boolean
  private readonly online: boolean
  private readonly options: OrderPeriodOptions
  private readonly now: Moment

  constructor(
    order: { online: boolean; date: string; start: string; end: string; when: { begin: Moment; end: Moment }; timezone: string },
    options: OrderPeriodOptions = {}
  ) {
    this.online = order.online
    this.options = options
    this.now = options.now ?? moment()
    this.multiDay = order.when.end.diff(order.when.begin, 'hours', true) >= 24

    if (order.online) {
      this.start = order.when.begin.clone().tz(order.timezone)
      this.end = order.when.end.clone().tz(order.timezone)
      return
    }

    this.start = moment.utc(`${order.date} ${order.start}`, 'YYYY-MM-DD HH:mm')
    const endDate = this.start.clone().add(order.when.end.diff(order.when.begin)).format('YYYY-MM-DD')
    this.end = moment.utc(`${endDate} ${order.end}`, 'YYYY-MM-DD HH:mm')
  }

  /**
   * «Сб, 19 декабря 10:00—12:00», or across days «Сб, 19 декабря, 10:00 — Вт, 22 декабря, 12:00»;
   * an online order's zone is `printZone()`'s. The short format drops the weekday and a one-day year:
   * «19 декабря 10:00–12:00». A year shows only where it is news: the start's when it is not this
   * year, the end's when it is not the start's.
   */
  print(): string {
    if (this.options.format !== 'short') return this.printLines().join(' ')
    if (this.multiDay) return this.printDays(this.shortDate()).join(' ')
    return this.printSplit().join('')
  }

  /**
   * The short `print()` as start, joiner and end, for a line that marks the start alone and keeps each end whole. Across
   * days the joiner holds the dash to the start, as the title breaks it: «10:00 —», then the end.
   */
  printSplit(): [start: string, joiner: string, end: string] {
    const date = this.shortDate()
    if (this.multiDay) {
      const [start, end] = this.dayEnds(date)
      return [start, '\u00a0— ', end]
    }
    // Word joiners around the dash: «10:00–12:00» must not break across lines.
    return [this.start.format(`${date} HH:mm`), '⁠–⁠', this.end.format('HH:mm')]
  }

  /** `print()` split where a title breaks it: the day, then its hours; across days, the start, then the end. */
  printLines(): [string, string] {
    const date = 'dd, D MMMM'
    if (this.multiDay) return this.printDays(date)

    const startDate = this.start.year() === this.now.year() ? date : `${date} YYYY`
    return [uppercaseFirstLetter(this.start.format(startDate)), `${this.start.format('HH:mm')}⁠—⁠${this.end.format('HH:mm')}`]
  }

  /** «+05:00» for an online order, shown on the viewer's clock; null offline, which keeps its own. */
  printZone(): string | null {
    return this.online ? this.end.format('Z') : null
  }

  private shortDate() {
    if (this.options.format !== 'short') throw new Error('OrderPeriod: the short print needs its format')
    return this.options.formatMessage(FORMATS_MESSAGES.date)
  }

  private printDays(date: string): [string, string] {
    const [start, end] = this.dayEnds(date)
    return [`${start} —`, end]
  }

  private dayEnds(date: string): [string, string] {
    const startDate = this.start.year() === this.now.year() ? date : `${date} YYYY`
    const endDate = this.end.year() === this.start.year() ? date : `${date} YYYY`
    return [uppercaseFirstLetter(this.start.format(`${startDate}, HH:mm`)), uppercaseFirstLetter(this.end.format(`${endDate}, HH:mm`))]
  }
}

export type OrderPeriodOptions = { now?: Moment } & ({ format?: 'full' } | { format: 'short'; formatMessage: (descriptor: MessageDescriptor) => string })
